// PLEASE READ - this component was 100% vibe-coded using GPT 5.1
// This is for internal, debug use only - this is not optimized for performance or accessibility
// and should not be used in any custom-facing features.

import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const OVERLAY_ROOT_ID = "__overlay_root__";

function ensureOverlayRoot() {
    let root = document.getElementById(OVERLAY_ROOT_ID);
    if (!root) {
        root = document.createElement("div");
        root.id = OVERLAY_ROOT_ID;
        Object.assign(root.style, {
            position: "fixed",
            inset: "0",
            pointerEvents: "none",
            zIndex: 999999,
        });
        document.body.appendChild(root);
    }
    return root;
}

function findFirstDom(childContainer: HTMLElement): HTMLElement | null {
    const walker = document.createTreeWalker(
        childContainer,
        NodeFilter.SHOW_ELEMENT,
    );
    return walker.nextNode() as HTMLElement | null;
}

export function OverlayHighlight({
    children,
    color = "rgba(0, 200, 255, 0.8)",
    borderWidth = 2,
    label,
}: {
    children: React.ReactNode;
    color?: string;
    borderWidth?: number;
    label?: string;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [targetNode, setTargetNode] = useState<HTMLElement | null>(null);
    const [rect, setRect] = useState<null | {
        top: number;
        left: number;
        width: number;
        height: number;
    } | null>(null);

    const overlayRoot =
        typeof window !== "undefined" ? ensureOverlayRoot() : null;

    useLayoutEffect(() => {
        if (containerRef.current) {
            const node = findFirstDom(containerRef.current);
            setTargetNode(node);
        }
    }, []);

    const updateRect = () => {
        if (!targetNode) return;
        const box = targetNode.getBoundingClientRect();
        setRect({
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
        });
    };

    useLayoutEffect(() => {
        if (!targetNode) return;

        updateRect();

        const resizeObs = new ResizeObserver(updateRect);
        resizeObs.observe(targetNode);

        window.addEventListener("scroll", updateRect, true);
        window.addEventListener("resize", updateRect);

        return () => {
            resizeObs.disconnect();
            window.removeEventListener("scroll", updateRect, true);
            window.removeEventListener("resize", updateRect);
        };
    }, [targetNode]);

    return (
        <>
            <div
                ref={containerRef}
                style={{
                    display: "contents",
                }}
            >
                {children}
            </div>

            {overlayRoot &&
                rect &&
                createPortal(
                    <>
                        {/* BORDER OVERLAY */}
                        <div
                            style={{
                                position: "fixed",
                                top: rect.top,
                                left: rect.left,
                                width: rect.width,
                                height: rect.height,
                                border: `${borderWidth}px solid ${color}`,
                                boxSizing: "border-box",
                            }}
                        />

                        {/* LABEL */}
                        {label && (
                            <div
                                style={{
                                    position: "fixed",
                                    top: rect.top,
                                    left: rect.left,
                                    transform: "translateY(-100%)",

                                    // background matches border color
                                    background: color,
                                    color: "white",

                                    padding: "2px 6px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",

                                    // rounded top, squared bottom
                                    borderTopLeftRadius: "4px",
                                    borderTopRightRadius: "4px",
                                    borderBottomLeftRadius: "0px",
                                    borderBottomRightRadius: "0px",

                                    pointerEvents: "none",
                                    userSelect: "none",
                                }}
                            >
                                {label}
                            </div>
                        )}
                    </>,
                    overlayRoot,
                )}
        </>
    );
}
